import type { WorkspaceNavigationGuardService } from '.';
import { effect, signal } from '@preact/signals-react';
import { DisposableBox } from '@thinkalpha/ui-core/DisposableBox';
import { inject, injectable } from 'src/features/ioc';
import type { NavigationGuard, NavigationIntent } from 'src/services/NavigationService';
import { parseNewWorkspaceSearch } from 'src/services/WorkspaceService/newWorkspace';
import { createRemoteSliceAction } from 'src/store/lib/createRemoteSlice';
import { confirmLeavingWorkspace } from 'src/store/overlays/confirmLeavingWorkspace';
import type { ReactBindings } from 'src/types/bindings';

type LeaveDecision = 'allow' | 'deny';

/** One decision about leaving the workspace, shared by every navigation that asks while it is pending. */
class LeaveConfirmation implements Disposable {
    readonly decision: Promise<LeaveDecision>;
    readonly #abortController = new AbortController();
    #resolve!: (decision: LeaveDecision) => void;

    constructor() {
        this.decision = new Promise<LeaveDecision>((resolve) => {
            this.#resolve = resolve;
        });
    }

    /** Aborted once decided; whatever the decision was waiting on stops with it. */
    get signal(): AbortSignal {
        return this.#abortController.signal;
    }

    settle(decision: LeaveDecision): void {
        if (this.signal.aborted) return;
        this.#abortController.abort();
        this.#resolve(decision);
    }

    [Symbol.dispose](): void {
        this.settle('deny');
    }
}

@injectable()
export class WorkspaceNavigationGuardServiceImpl implements WorkspaceNavigationGuardService, NavigationGuard {
    readonly requiresConfirmation = signal(true);
    readonly #confirmation = new DisposableBox<LeaveConfirmation>();
    readonly #disposeProtectionEffect: () => void;
    #registration: Disposable | null = null;

    constructor(
        @inject('Store') private readonly store: ReactBindings['Store'],
        @inject('NavigationService')
        private readonly navigationService: ReactBindings['NavigationService'],
        @inject('WorkspaceServiceProvider')
        private readonly workspaceServiceProvider: ReactBindings['WorkspaceServiceProvider'],
    ) {
        this.#disposeProtectionEffect = effect(() => {
            // Unsaved changes need protecting, and so does a save in flight: leaving waits for it.
            const { currentWorkspace, currentWorkspaceStatus } = this.store.signal.value.workspace;
            const protects = Boolean(currentWorkspace?.isDirty) || currentWorkspaceStatus === 'saving';
            if (protects && !this.#registration) {
                this.#registration = this.navigationService.registerGuard(this);
            } else if (!protects && this.#registration) {
                this.#registration[Symbol.dispose]();
                this.#registration = null;
            }
        });
    }

    confirm(intent: NavigationIntent): Promise<LeaveDecision> {
        const pending = this.#confirmation.current;
        if (pending) return pending.decision;

        if (this.#staysInCurrentWorkspace(intent)) {
            return Promise.resolve('allow');
        }

        const confirmation = new LeaveConfirmation();
        this.#confirmation.next(confirmation);
        void this.#decide(confirmation);
        return confirmation.decision;
    }

    async #decide(confirmation: LeaveConfirmation): Promise<void> {
        // Save-then-navigate: a save already in flight is what leaving waits for, and this
        // navigation takes over its post-save navigation. Prompt only for what remains unsaved.
        await this.workspaceServiceProvider().settleBeforeLeaving();
        if (confirmation.signal.aborted) return;
        if (!this.store.getState().workspace.currentWorkspace?.isDirty) {
            this.#settle(confirmation, 'allow');
            return;
        }

        const unsubscribe = this.store.subscribe(() => {
            const state = this.store.getState().remoteSlice.confirmLeavingWorkspace as {
                readonly isOpen: boolean;
                readonly lastCloseResult: 'cancel' | 'dont-save' | 'save' | 'saved' | null;
            };
            // This guard owns the modal opening, so the first close action
            // is authoritative even when open and close dispatch back-to-back.
            if (state.isOpen || state.lastCloseResult === null) return;

            this.#settle(confirmation, state.lastCloseResult === 'cancel' ? 'deny' : 'allow');
        });
        confirmation.signal.addEventListener('abort', unsubscribe, { once: true });

        this.store.dispatch(createRemoteSliceAction(confirmLeavingWorkspace, 'openModal'));
    }

    #settle(confirmation: LeaveConfirmation, decision: LeaveDecision): void {
        confirmation.settle(decision);
        if (this.#confirmation.current === confirmation) this.#confirmation.release();
    }

    #staysInCurrentWorkspace(intent: NavigationIntent): boolean {
        if (intent.kind !== 'navigate' || !intent.to) return false;

        const current = this.store.signal.value.workspace.currentWorkspace;
        const [, route, destinationWorkspaceId] = intent.to.pathname.split('/');
        if (route !== 'dashboard' || !current) return false;
        if (destinationWorkspaceId === 'new') {
            // /dashboard/new names the unsaved workspace on screen unless it carries a different request.
            const request = parseNewWorkspaceSearch(intent.to.search);
            return current.id === 'new' && (!request || request.id === current.newWorkspaceRequestId);
        }
        return destinationWorkspaceId === current.id;
    }

    [Symbol.dispose](): void {
        this.#disposeProtectionEffect();
        this.#confirmation[Symbol.dispose]();
        this.#registration?.[Symbol.dispose]();
        this.#registration = null;
    }
}
